/**
 * Transfer Print Utility
 * Generates a printable Transfer Letter / Registry document
 */

const printTransferLetter = ({ transferData, previousOwner, newOwner, plot, society }) => {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-PK", {
            style: "currency",
            currency: "PKR",
            minimumFractionDigits: 0
        }).format(amount || 0).replace("PKR", "Rs.");
    };

    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleDateString("en-PK", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });
        return new Date(dateString).toLocaleDateString("en-PK", {
            day: "2-digit",
            month: "long",
            year: "numeric"
        });
    };

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Transfer Letter - ${plot?.plot_number || 'Plot'}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          color: #1e293b;
          background: white;
          padding: 40px;
        }
        
        .header {
          text-align: center;
          border-bottom: 3px solid #4f46e5;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        
        .header h1 {
          font-size: 28px;
          color: #4f46e5;
          margin-bottom: 5px;
        }
        
        .header p {
          font-size: 14px;
          color: #64748b;
        }
        
        .title {
          text-align: center;
          margin: 30px 0;
        }
        
        .title h2 {
          font-size: 22px;
          color: #1e293b;
          text-transform: uppercase;
          letter-spacing: 2px;
          border: 2px solid #4f46e5;
          display: inline-block;
          padding: 10px 30px;
        }
        
        .ref-date {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          font-size: 14px;
        }
        
        .section {
          margin-bottom: 25px;
        }
        
        .section-title {
          font-size: 14px;
          font-weight: 600;
          color: #4f46e5;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 5px;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        
        .info-item {
          display: flex;
          gap: 8px;
        }
        
        .info-label {
          font-weight: 500;
          color: #64748b;
          min-width: 120px;
        }
        
        .info-value {
          font-weight: 600;
          color: #1e293b;
        }
        
        .transfer-box {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border: 2px solid #4f46e5;
          border-radius: 12px;
          padding: 20px;
          margin: 30px 0;
        }
        
        .transfer-box .arrow {
          text-align: center;
          font-size: 24px;
          color: #4f46e5;
          margin: 15px 0;
        }
        
        .owner-card {
          background: white;
          padding: 15px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
        }
        
        .owner-card h4 {
          font-size: 12px;
          color: #4f46e5;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        
        .owner-card .name {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
        }
        
        .owner-card .details {
          font-size: 13px;
          color: #64748b;
          margin-top: 5px;
        }
        
        .fee-section {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 15px;
          margin: 20px 0;
        }
        
        .fee-section .fee-title {
          font-size: 12px;
          color: #92400e;
          text-transform: uppercase;
          font-weight: 600;
        }
        
        .fee-section .fee-amount {
          font-size: 24px;
          font-weight: 700;
          color: #b45309;
        }
        
        .declaration {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
          margin: 30px 0;
          font-size: 13px;
          color: #475569;
          text-align: justify;
        }
        
        .signatures {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 40px;
          margin-top: 60px;
        }
        
        .signature-box {
          text-align: center;
        }
        
        .signature-line {
          border-top: 1px solid #1e293b;
          margin-bottom: 8px;
          padding-top: 10px;
        }
        
        .signature-label {
          font-size: 12px;
          color: #64748b;
        }
        
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          text-align: center;
          font-size: 11px;
          color: #94a3b8;
        }
        
        @media print {
          body { padding: 20px; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${society?.name || 'AL NOOR DEVELOPERS'}</h1>
        <p>${society?.location || 'Real Estate & Property Development'}</p>
      </div>
      
      <div class="title">
        <h2>Transfer Letter</h2>
      </div>
      
      <div class="ref-date">
        <span><strong>Ref #:</strong> TRF-${Date.now().toString().slice(-8)}</span>
        <span><strong>Date:</strong> ${formatDate(transferData?.transfer_date)}</span>
      </div>
      
      <div class="section">
        <div class="section-title">Plot Details</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Plot Number:</span>
            <span class="info-value">${plot?.plot_number || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Block:</span>
            <span class="info-value">${plot?.block || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Size:</span>
            <span class="info-value">${plot?.size || plot?.marla + ' Marla' || 'N/A'}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Category:</span>
            <span class="info-value">${plot?.category || 'Standard'}</span>
          </div>
        </div>
      </div>
      
      <div class="transfer-box">
        <div class="owner-card">
          <h4>Previous Owner (Transferor)</h4>
          <div class="name">${previousOwner?.name || 'N/A'}</div>
          <div class="details">
            CNIC: ${previousOwner?.contactInfo?.cnic || 'N/A'} | 
            Phone: ${previousOwner?.contactInfo?.phone || 'N/A'}
          </div>
        </div>
        
        <div class="arrow">⬇ TRANSFER TO ⬇</div>
        
        <div class="owner-card">
          <h4>New Owner (Transferee)</h4>
          <div class="name">${newOwner?.name || 'N/A'}</div>
          <div class="details">
            CNIC: ${newOwner?.contactInfo?.cnic || 'N/A'} | 
            Phone: ${newOwner?.contactInfo?.phone || 'N/A'}
          </div>
        </div>
      </div>
      
      ${transferData?.transfer_fee > 0 ? `
        <div class="fee-section">
          <div class="fee-title">Transfer Fee ${transferData?.fee_type === 'percentage' ? `(${transferData?.fee_percentage}%)` : '(Fixed)'}</div>
          <div class="fee-amount">${formatCurrency(transferData?.transfer_fee)}</div>
        </div>
      ` : ''}
      
      <div class="declaration">
        <p>This is to certify that the ownership of <strong>Plot No. ${plot?.plot_number}</strong>, 
        located in <strong>${plot?.block || 'Block'}</strong> of <strong>${society?.name || 'the Society'}</strong>, 
        has been officially transferred from <strong>${previousOwner?.name}</strong> (CNIC: ${previousOwner?.contactInfo?.cnic || 'N/A'}) 
        to <strong>${newOwner?.name}</strong> (CNIC: ${newOwner?.contactInfo?.cnic || 'N/A'}) 
        on <strong>${formatDate(transferData?.transfer_date)}</strong>.</p>
        <p style="margin-top: 15px;">
          All rights, interests, and obligations associated with the aforementioned plot are hereby transferred 
          to the new owner with immediate effect. This letter serves as official documentation of the transfer 
          of ownership as per the society's records.
        </p>
      </div>
      
      <div class="signatures">
        <div class="signature-box">
          <div class="signature-line"></div>
          <div class="signature-label">Previous Owner</div>
        </div>
        <div class="signature-box">
          <div class="signature-line"></div>
          <div class="signature-label">New Owner</div>
        </div>
        <div class="signature-box">
          <div class="signature-line"></div>
          <div class="signature-label">Authorized Signatory</div>
        </div>
      </div>
      
      <div class="footer">
        <p>This is a computer-generated document. For verification, please contact the society office.</p>
        <p>Generated on: ${new Date().toLocaleString('en-PK')}</p>
      </div>
    </body>
    </html>
  `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
        printWindow.print();
    }, 250);
};

export default printTransferLetter;
